import { useId, useState, type KeyboardEvent, type PointerEvent } from 'react'

import type { TimingCell } from '@/api/revenue'
import { formatInteger, formatValue, type FormatOptions } from '@/design'
import { ChartDataTable } from '@/design/chartParts'

import { hourLabel, WEEKDAY_LONG, WEEKDAY_SHORT } from '../format'

/** The ramp's steps: one hue, dark (little) to light (most) on the dark surface. */
export const HEAT_STEPS = 6

/** A cell's step: 0 for none (or a net loss), else 1–6 by its share of the busiest cell. */
export function heatStep(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0
  return Math.min(HEAT_STEPS, Math.max(1, Math.ceil((value / max) * HEAT_STEPS)))
}

/**
 * Revenue by weekday and hour, the tenant's wall clock: 168 cells, Monday 00:00 first, each
 * shaded by its step on a single-hue ramp. One tab stop: the arrow keys (and the pointer)
 * move a cell cursor whose tooltip, announced politely, gives the cell's revenue and
 * orders; the whole grid is a visually hidden table too.
 */
export function HeatMap({ grid, money, timezone }: { grid: readonly TimingCell[]; money: FormatOptions; timezone: string }) {
  const [active, setActive] = useState<number | null>(null)
  const tooltipId = useId()
  const cells = Array.from({ length: 168 }, (_, index) => grid.find((cell) => cell.weekday * 24 + cell.hour === index) ?? null)
  const max = Math.max(0, ...grid.map((cell) => cell.revenue))
  const activeCell = active === null ? null : cells[active]

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const moves: Record<string, (index: number) => number> = {
      ArrowRight: (index) => (index % 24 === 23 ? index : index + 1),
      ArrowLeft: (index) => (index % 24 === 0 ? index : index - 1),
      ArrowDown: (index) => (index + 24 < 168 ? index + 24 : index),
      ArrowUp: (index) => (index - 24 >= 0 ? index - 24 : index),
      Home: (index) => index - (index % 24),
      End: (index) => index - (index % 24) + 23,
    }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    setActive((index) => move(index ?? 0))
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-cell]')
    if (target?.dataset.cell) setActive(Number(target.dataset.cell))
  }

  const describe = (index: number) => {
    const cell = cells[index]
    const weekday = Math.floor(index / 24)
    const hour = index % 24
    const when = `${WEEKDAY_LONG[weekday]} ${hourLabel(hour)}–${hourLabel(hour + 1)}`
    return cell ? `${when}: ${formatValue(cell.revenue, 'currency', money)} · ${formatInteger(cell.orders, money)} orders` : `${when}: —`
  }

  return (
    <figure className="rv-heat">
      <div
        className="rv-heat__plot"
        role="group"
        tabIndex={0}
        aria-label={`Revenue by weekday and hour, ${timezone}. Use the arrow keys to read each hour.`}
        aria-describedby={tooltipId}
        onKeyDown={onKeyDown}
        onBlur={() => setActive(null)}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setActive(null)}
      >
        <div className="rv-heat__grid" aria-hidden="true">
          <span />
          {Array.from({ length: 24 }, (_, hour) => (
            <span key={`h-${hour}`} className="rv-heat__hour">
              {hour % 3 === 0 ? String(hour).padStart(2, '0') : ''}
            </span>
          ))}
          {WEEKDAY_SHORT.map((weekday, row) => (
            <RowCells key={weekday} weekday={weekday} row={row} cells={cells} max={max} active={active} />
          ))}
        </div>
        <div
          id={tooltipId}
          className="rv-heat__tooltip"
          aria-live="polite"
          data-visible={activeCell !== null && active !== null ? 'true' : 'false'}
          style={
            active !== null
              ? { left: `${Math.min(82, Math.max(18, (((active % 24) + 0.5) / 24) * 100))}%`, top: `${((Math.floor(active / 24) + 1) / 8) * 100}%` }
              : undefined
          }
        >
          {active !== null ? describe(active) : null}
        </div>
      </div>
      <figcaption className="rv-legend rv-heat__legend">
        <span className="rv-legend__item">
          <span className="rv-heat__swatch" data-step={0} aria-hidden="true" />
          None
        </span>
        <span className="rv-legend__item">Low</span>
        {Array.from({ length: HEAT_STEPS }, (_, step) => (
          <span key={step} className="rv-heat__swatch" data-step={step + 1} aria-hidden="true" />
        ))}
        <span className="rv-legend__item">High</span>
      </figcaption>
      <ChartDataTable
        title={`Revenue by weekday and hour (${timezone})`}
        columns={['Weekday', ...Array.from({ length: 24 }, (_, hour) => hourLabel(hour))]}
        rows={WEEKDAY_LONG.map((weekday, row) => ({
          key: weekday,
          name: weekday,
          cells: Array.from({ length: 24 }, (_, hour) => {
            const cell = cells[row * 24 + hour]
            return cell ? formatValue(cell.revenue, 'currency', money) : '—'
          }),
        }))}
      />
    </figure>
  )
}

function RowCells({
  weekday,
  row,
  cells,
  max,
  active,
}: {
  weekday: string
  row: number
  cells: readonly (TimingCell | null)[]
  max: number
  active: number | null
}) {
  return (
    <>
      <span className="rv-heat__day">{weekday}</span>
      {Array.from({ length: 24 }, (_, hour) => {
        const index = row * 24 + hour
        const cell = cells[index]
        return (
          <span
            key={index}
            className="rv-heat__cell"
            data-cell={index}
            data-testid="heat-cell"
            data-step={cell ? heatStep(cell.revenue, max) : 0}
            data-active={active === index || undefined}
          />
        )
      })}
    </>
  )
}
